import { defineComponent as _defineComponent } from 'vue'
import { toDisplayString as _toDisplayString, createElementVNode as _createElementVNode, createVNode as _createVNode, unref as _unref, createTextVNode as _createTextVNode, withCtx as _withCtx, openBlock as _openBlock, createBlock as _createBlock, createCommentVNode as _createCommentVNode, Fragment as _Fragment, createElementBlock as _createElementBlock } from "vue"

const _hoisted_1 = { class: "multicolumn-view" }
const _hoisted_2 = { class: "view-full-span" }
const _hoisted_3 = { class: "bowler-profile-data-section" }
const _hoisted_4 = { class: "bowler-profile-data-section" }
const _hoisted_5 = { class: "data-section-heading" }
const _hoisted_6 = { class: "bowler-profile-data-section" }

import isNil from 'lodash/isNil'
import { computed, onBeforeMount, ref } from 'vue'
import { useRouter } from 'vue-router'

import FormButton from '@/components/common/FormButton.vue'
import NotificationBanner from '@/components/common/NotificationBanner.vue'
import ValueWithLabel from '@/components/common/ValueWithLabel.vue'
import BowlerEditableInfoForm from '@/components/forms/BowlerEditableInfoForm.vue'
import { useBowlerStore } from '@/store/bowler-app/bowler'
import { formatBoolean, formatValue } from '@/utils/format'

interface ContactDetails {
  email: string
  phoneNumber: string
  streetAddress: string
  postalCode: string
  city: string
  country: string
  isMarketingPermitted: boolean
}


export default /*@__PURE__*/_defineComponent({
  __name: 'BowlerProfileView',
  setup(__props) {

const router = useRouter()
const bowler = useBowlerStore()

const isEditMode = ref<boolean>(false)
const isUpdateSuccessful = ref<boolean>(false)

/* actions */

const editBowlerInfo = () => {
  isUpdateSuccessful.value = false
  isEditMode.value = true
}

/* hooks */

onBeforeMount(async (): Promise<void> => {
  if (isNil(bowler.id)) {
    await router.push({ path: '/' })
  }
})

/* computed */

const bowlerContactDetails = computed<ContactDetails>(
  (): ContactDetails => bowler as ContactDetails,
)

const bowlerFullName = computed<string>((): string => {
  const firstName = bowler.firstName ?? null
  const lastName = bowler.lastName ?? null

  if (!isNil(firstName) && !isNil(lastName)) {
    return `${firstName} ${lastName}`
  }

  return formatValue(firstName ?? lastName)
})

return (_ctx: any,_cache: any) => {
  return (_openBlock(), _createElementBlock("main", _hoisted_1, [
    _createElementVNode("h1", _hoisted_2, _toDisplayString(_ctx.$t('section-titles.profile')), 1),
    _createElementVNode("div", _hoisted_3, [
      _createElementVNode("h2", null, _toDisplayString(_ctx.$t('section-titles.general-information')), 1),
      _createVNode(ValueWithLabel, {
        value: bowlerFullName.value,
        label: _ctx.$t('field-labels.first-and-last-names')
      }, null, 8, ["value", "label"]),
      _createVNode(ValueWithLabel, {
        type: "date",
        value: _unref(bowler).birthDate,
        label: _ctx.$t('field-labels.date-of-birth')
      }, null, 8, ["value", "label"]),
      _createVNode(ValueWithLabel, {
        type: "gender",
        value: _unref(bowler).gender,
        label: _ctx.$t('field-labels.gender')
      }, null, 8, ["value", "label"])
    ]),
    _createElementVNode("div", _hoisted_4, [
      _createElementVNode("div", _hoisted_5, [
        _createElementVNode("h2", null, _toDisplayString(_ctx.$t('section-titles.contact-information')), 1),
        (!isEditMode.value)
          ? (_openBlock(), _createBlock(FormButton, {
              key: 0,
              "is-small": "",
              onClick: editBowlerInfo
            }, {
              default: _withCtx(() => [
                _createTextVNode(_toDisplayString(_ctx.$t('general.edit')), 1)
              ]),
              _: 1
            }))
          : _createCommentVNode("", true)
      ]),
      (isEditMode.value)
        ? (_openBlock(), _createBlock(BowlerEditableInfoForm, {
            key: 0,
            "initial-data": bowlerContactDetails.value,
            onDismiss: _cache[0] || (_cache[0] = ($event: any) => (isEditMode.value = false)),
            onSuccess: _cache[1] || (_cache[1] = ($event: any) => (isUpdateSuccessful.value = true))
          }, null, 8, ["initial-data"]))
        : (_openBlock(), _createElementBlock(_Fragment, { key: 1 }, [
            (isUpdateSuccessful.value)
              ? (_openBlock(), _createBlock(NotificationBanner, {
                  key: 0,
                  "is-success": "",
                  message: "Your details have been updated successfully"
                }))
              : _createCommentVNode("", true),
            _createVNode(ValueWithLabel, {
              value: _unref(bowler).email,
              label: _ctx.$t('field-labels.email')
            }, null, 8, ["value", "label"]),
            _createVNode(ValueWithLabel, {
              value: _unref(bowler).phoneNumber,
              label: _ctx.$t('field-labels.phone')
            }, null, 8, ["value", "label"]),
            _createVNode(ValueWithLabel, {
              value: _unref(bowler).streetAddress,
              label: _ctx.$t('field-labels.street-address')
            }, null, 8, ["value", "label"]),
            _createVNode(ValueWithLabel, {
              value: _unref(bowler).postalCode,
              label: _ctx.$t('field-labels.postal-code')
            }, null, 8, ["value", "label"]),
            _createVNode(ValueWithLabel, {
              value: _unref(bowler).city,
              label: _ctx.$t('field-labels.city')
            }, null, 8, ["value", "label"]),
            _createVNode(ValueWithLabel, {
              value: _unref(formatBoolean)(_unref(bowler).isMarketingPermitted),
              label: _ctx.$t('field-labels.is-marketing-permitted')
            }, null, 8, ["value", "label"])
          ], 64))
    ]),
    _createElementVNode("div", _hoisted_6, [
      _createElementVNode("h2", null, _toDisplayString(_ctx.$t('section-titles.membership')), 1),
      _createVNode(ValueWithLabel, {
        value: _unref(bowler).activeMembership?.clubName,
        label: _ctx.$t('field-labels.club')
      }, null, 8, ["value", "label"]),
      _createVNode(ValueWithLabel, {
        value: _unref(bowler).activeMembership?.associationName,
        label: _ctx.$t('field-labels.association')
      }, null, 8, ["value", "label"]),
      _createVNode(ValueWithLabel, {
        type: "date",
        value: _unref(bowler).firstRegistrationDate,
        label: _ctx.$t('field-labels.registration-date')
      }, null, 8, ["value", "label"]),
      _createVNode(ValueWithLabel, {
        value: _unref(bowler).memberNumber,
        label: _ctx.$t('field-labels.code')
      }, null, 8, ["value", "label"]),
      _createVNode(ValueWithLabel, {
        value: _unref(formatValue)(_unref(bowler).currentClass),
        label: _ctx.$t('field-labels.class')
      }, null, 8, ["value", "label"])
    ])
  ]))
}
}

})